import { watch } from 'vue'
import { listen } from '@tauri-apps/api/event'
import { ConnectionState } from 'livekit-client'
import { invoke } from '~/lib/tauriInvoke'
import { isTauriRuntime } from '~/lib/platform/runtime'
import { liveKitManager } from '~/lib/livekit/manager'
import { useGroupsStore } from '~/stores/groups'

// iOS: registers the voice channel call with the system through CallKit while connected (see
// src-tauri/gen/apple/Sources/app/CallKitBridge.swift for why — without it another app's audio
// interrupted the call's audio in the background and iOS then suspended the app, dropping the call).
//
// The call is started once connected to a voice channel, renamed if the channel changes, and ended
// on leaving. The system call UI (lock screen, Dynamic Island) can hang up or mute too — those come
// back here as events and go through the same store actions as the in-app buttons.

type CallKitEvent = { type: 'ended' } | { type: 'muted'; muted: boolean } | { type: 'error'; message?: string }

const isIOS = typeof navigator !== 'undefined' && /iPhone|iPad|iPod/i.test(navigator.userAgent)

export default defineNuxtPlugin(() => {
  if (!isIOS || !isTauriRuntime()) return

  const groupsStore = useGroupsStore()
  const state = liveKitManager.state
  let callActive = false

  function callTitle(channelId: string) {
    const groupId = groupsStore.groupIdForChannel(channelId) ?? groupsStore.selectedGroupId
    const channelName = groupId ? groupsStore.findChannel(groupId, channelId)?.name : undefined
    return channelName || 'Голосовой канал'
  }

  // Connected to a channel (not just trying to) -> call up; anything else with no channel -> call down.
  // Reconnecting keeps the call: the channel is still the same one.
  watch(
    () => [state.activeVoiceChannelId, state.roomState] as const,
    ([channelId, roomState]) => {
      if (channelId && roomState === ConnectionState.Connected) {
        callActive = true
        void invoke('ios_callkit_start', { title: callTitle(channelId) }).catch((error) => {
          console.warn('[CallKit] start failed', error)
        })
        void invoke('ios_callkit_set_muted', { muted: state.isMicMuted }).catch(() => {})
      } else if (!channelId && callActive) {
        callActive = false
        void invoke('ios_callkit_end').catch((error) => console.warn('[CallKit] end failed', error))
      }
    },
    { immediate: true },
  )

  watch(() => state.isMicMuted, (muted) => {
    if (callActive) void invoke('ios_callkit_set_muted', { muted }).catch(() => {})
  })

  void listen<CallKitEvent>('ios-callkit', ({ payload }) => {
    if (payload.type === 'ended') {
      callActive = false
      if (state.activeVoiceChannelId) void groupsStore.disconnectChannel()
    } else if (payload.type === 'muted') {
      if (payload.muted !== state.isMicMuted) void groupsStore.toggleMic()
    } else if (payload.type === 'error') {
      callActive = false
      console.warn('[CallKit]', payload.message)
    }
  })
})
